import { Skeleton, Stack, Typography } from '@mui/material';
import { type ReactNode, useContext } from 'react';

import { UserContext } from '~/auth/userContext';
import { HeaderField } from '~/components/common/HeaderField';
import { LiftAITextLink } from '~/components/utils/helpers';
import type { Invoice } from '~/data/schemas/invoice';
import { invoiceTypeToLabelMap, isCredit } from '~/data/schemas/invoice';
import { canAccess, NAVIGATE_TO_PROPERTY_PAGE } from '~/utils/role';
import { buildUrlForPropertySpending } from '~/utils/urlBuilder';

/**
 * Who the invoice belongs to — property, service provider, type, consultant — in one row
 * under the identity block, with `children` for whatever else belongs in it.
 *
 * In the header rather than the Overview tab: these never change and are the context
 * every tab is read against.
 */
export function InvoiceIdentityFields({
  invoice,
  children,
  showConsultant = true,
}: {
  invoice: Invoice | undefined;
  children?: ReactNode;
  showConsultant?: boolean;
}) {
  const { userDetails } = useContext(UserContext);
  const canLinkToProperty = canAccess(userDetails.role, NAVIGATE_TO_PROPERTY_PAGE);

  const value = (text: string | null | undefined, fallback = 'N/A') =>
    invoice ? (
      <Typography variant="body2">{text || fallback}</Typography>
    ) : (
      <Skeleton width={110} />
    );

  const property = invoice?.property;

  return (
    <Stack direction="row" flexWrap="wrap" useFlexGap columnGap={5} rowGap={1.5}>
      <HeaderField label="Property" caption={property?.address}>
        {invoice ? (
          canLinkToProperty && property?.id ? (
            <LiftAITextLink
              variant="body2"
              label={property.name}
              href={buildUrlForPropertySpending({ propertyId: property.id })}
            />
          ) : (
            <Typography variant="body2">{property?.name || 'N/A'}</Typography>
          )
        ) : (
          <Skeleton width={140} />
        )}
      </HeaderField>

      <HeaderField label="Service Provider">{value(invoice?.serviceProvider?.name)}</HeaderField>

      {/* A credit's type comes from what it applies to, so it isn't shown. */}
      {invoice && isCredit(invoice) ? null : (
        <HeaderField label="Type">
          {value(invoice ? invoiceTypeToLabelMap.get(invoice.type) : undefined)}
        </HeaderField>
      )}

      {showConsultant ? (
        <HeaderField label="Consultant">{value(invoice?.consultant?.fullName)}</HeaderField>
      ) : null}

      {children}
    </Stack>
  );
}
